import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { FaArrowLeft } from "react-icons/fa";
import { MdAddCircle, MdRemoveCircle } from "react-icons/md";

import { PassengerCard } from "../components/tripSheet/PassengerCard";
import { TRIP_DETAILS_LABELS } from "../components/constants/tripsheet";
import { useAtomValue, useSetAtom } from "jotai";
import { alertAtom, globalLoaderAtom } from "../jotaiState/globalStateAtoms";
import { apiGet } from "../helper/utils";
import { useEffect } from "react";
import { tripAtom } from "../components/JotaiStates/commonStateAtoms";
import useLogin from "../components/common/useLogin";
import { TripDetails, TripSheetUrl } from "../helper/apiUrls";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export const TripSheet = () => {
  const { login } = useLogin();
  const navigate = useNavigate();
  const [tripExpanded, setTripExpanded] = useState(false);
  const trip = useAtomValue(tripAtom);
  const setAlert = useSetAtom(alertAtom);
  const setLoader = useSetAtom(globalLoaderAtom);
  const [TripDetail, setTripDetails] = useState({});
  const [tripList, setTripList] = useState([]);

  const getTripSheet = () => {
    setLoader(true);
    const payload = {
      intFromCityID: trip.FromCityId,
      intToCityID: trip.ToCityId,
      intTripID: trip.TripID,
      dtChartDate: trip.ChartDate,
      intUserID: login.DriverID,
      intCompanyID: login.CompanyID,
    };
    apiGet(TripSheetUrl, payload)
      .then((res) => {
        if (!res.IsSuccess) {
          throw new Error(res.ErrorMessage || "Error fetching trip sheet");
        }
        const data = JSON.parse(res.Data);
        setTripList(groupByPNR(data));
      })
      .catch((err) => {
        setAlert({ show: true, msg: err.message, nextId: "" });
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const getTripDetails = () => {
    setLoader(true);
    const payload = {
      intFromCityID: trip.FromCityId,
      intToCityID: trip.ToCityId,
      intTripID: trip.TripID,
      JourneyDate: trip.ChartDate,
      intUserID: login.DriverID,
      intCompanyID: login.CompanyID,
    };
    apiGet(TripDetails, payload)
      .then((res) => {
        if (!res.IsSuccess) {
          throw new Error(res.ErrorMessage || "Error fetching trip details");
        }
        const data = JSON.parse(res.Data);
        const { Table, Table1, Table2 } = data;
        const tripDetail = {
          ...Table[0],
          DriverCode: login.DriverCode,
          DriverName: login.DriverName,
          PrintTime: dayjs().format("DD-MMM-YYYY HH:mm A"),
          RouteName: `${Table[0].FromCityShortName} - ${Table[0].ToCityShortName}`,
        };
        const TotalSeatsCount = Table.reduce((acc, curr) => {
          return acc + +curr.TotalSeatsCount;
        }, 0);
        tripDetail.TotalSeatsCount = TotalSeatsCount;
        const TotalFare = Table.reduce((acc, curr) => {
          return acc + +curr.TotalFare;
        }, 0);
        tripDetail.TotalFare = TotalFare;
        const LuggageAmount = Table1.reduce((acc, curr) => {
          return acc + +curr.Amount;
        }, 0);
        tripDetail.LuggageAmount = LuggageAmount;
        const ConcessionAmount = Table2.reduce((acc, curr) => {
          return acc + +curr.Amount;
        }, 0);
        tripDetail.ConcessionAmount = ConcessionAmount;
        const TotalAmount = TotalFare + LuggageAmount - ConcessionAmount;
        tripDetail.TotalAmount = TotalAmount;
        setTripDetails(tripDetail);
      })
      .catch((err) => {
        setAlert({ show: true, msg: err.message, nextId: "" });
      })
      .finally(() => {
        setLoader(false);
      });
  };
  function groupByPNR(data) {
    const grouped = data.reduce((acc, curr) => {
      const { PNRNo, SeatNoWithGender, SeatwiseFare, PaxName, ...rootFields } =
        curr;
      if (!acc[PNRNo]) {
        acc[PNRNo] = {
          ...rootFields,
          PNRNo,
          passengers: [],
        };
      }
      acc[PNRNo].passengers.push({
        SeatNoWithGender,
        SeatwiseFare,
        PaxName,
      });
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => {
      const dateA = dayjs(a.pickupdatetime, "MM-DD-YYYY HH:mm:ss", true);
      const dateB = dayjs(b.pickupdatetime, "MM-DD-YYYY HH:mm:ss", true);
      if (!dateA.isValid() && !dateB.isValid()) return 0;
      if (!dateA.isValid()) return 1;
      if (!dateB.isValid()) return -1;
      return dateA.valueOf() - dateB.valueOf();
    });
  }

  useEffect(() => {
    if (trip.TripID) {
      getTripSheet();
      getTripDetails();
    }
  }, [trip]);

  return (
    <Box className="trip-details-container">
      <Box
        display="flex"
        className="trip-details-header"
        alignItems="center"
        gap={1}
        mb={0}
      >
        <FaArrowLeft className="back-icon" onClick={() => navigate(-1)} />
        <Typography variant="h6" fontWeight={600} color="white">
          Trip Sheet
        </Typography>
      </Box>
      <Box sx={{ boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          onClick={() => setTripExpanded((prev) => !prev)}
        >
          <IconButton color="primary">
            {tripExpanded ? <MdRemoveCircle /> : <MdAddCircle />}
          </IconButton>

          <Typography color="primary" fontWeight={600}>
            Trip Details ({trip.TripID})
          </Typography>
        </Box>

        {/* Trip Details Table */}
        {tripExpanded && (
          <Box
            bgcolor="table.headBackground"
            sx={{
              borderRadius: 2,
            }}
          >
            <Table size="small">
              <TableBody>
                {TRIP_DETAILS_LABELS.map(({ label, key }) => (
                  <TableRow key={key}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        whiteSpace: "nowrap",
                        padding: "4px 4px 4px 16px",
                        verticalAlign: "text-top",
                      }}
                    >
                      {label}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: 10,
                        fontWeight: 600,
                        padding: "4px 0px",
                        verticalAlign: "text-top",
                      }}
                    >
                      :
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        padding: "4px 0px 4px 4px",
                        verticalAlign: "text-top",
                      }}
                    >
                      {TripDetail[key]}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
      <Typography variant="subtitle1" fontWeight={600} p={1} pl={2}>
        Passenger List
      </Typography>

      <Box display="flex" flexDirection="column" gap={0}>
        {tripList.map((trip) => (
          <PassengerCard key={trip.PNRNo} trip={trip} onCardClick={() => {}} />
        ))}
      </Box>
    </Box>
  );
};
